import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge, Button, Group, Kbd, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCards, IconCheck, IconX } from '@tabler/icons-react';

import { useIsMobile } from '@/hooks/useIsMobile';
import { parseDeadCards } from '@/lib/deadCardParser';
import { useChartStore } from '@/store/chartStore';

interface DeadCardModalProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal for selecting dead/removed cards
 */
export function DeadCardModal({ opened, onClose }: DeadCardModalProps) {
  const deadCards = useChartStore((state) => state.deadCards);
  const deadCardsString = useChartStore((state) => state.deadCardsString);
  const setDeadCards = useChartStore((state) => state.setDeadCards);
  const clearDeadCards = useChartStore((state) => state.clearDeadCards);

  const [inputValue, setInputValue] = useState('');
  const [debouncedValue] = useDebouncedValue(inputValue, 150);
  const inputRef = useRef<HTMLInputElement>(null);

  // Pre-populate with existing cards when modal opens
  useEffect(() => {
    if (opened) {
      setInputValue(deadCardsString);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [opened, deadCardsString]);

  // Parse the input
  const parsedCards = useMemo(() => {
    return parseDeadCards(debouncedValue);
  }, [debouncedValue]);

  const handleApply = useCallback(() => {
    setDeadCards(parsedCards, inputValue.trim());
    onClose();
  }, [parsedCards, inputValue, setDeadCards, onClose]);

  const handleClear = useCallback(() => {
    clearDeadCards();
    setInputValue('');
    onClose();
  }, [clearDeadCards, onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleApply();
      }
    },
    [handleApply],
  );

  const hasDeadCards = deadCards.size > 0;
  const isMobile = useIsMobile();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconCards size={20} />
          <Text fw={500}>Dead Cards</Text>
        </Group>
      }
      centered={!isMobile}
      size="md"
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      styles={isMobile ? { inner: { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' } } : undefined}
    >
      <Stack gap="md">
        <TextInput
          ref={inputRef}
          placeholder="e.g., Ac, Kh, 7d, 2s"
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          size="md"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          styles={{
            input: {
              fontFamily: 'monospace',
              fontSize: '1rem',
            },
          }}
        />

        {/* Parsed cards display */}
        <ParsedCardsDisplay cards={parsedCards} inputValue={inputValue} />

        {/* Examples */}
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Examples:
          </Text>
          {['Ac', 'Kh', '7d', '2s'].map((example) => (
            <Badge
              key={example}
              component="button"
              size="sm"
              variant="light"
              color="orange"
              style={{ cursor: 'pointer', fontFamily: 'monospace', textTransform: 'none' }}
              onClick={() => {
                const current = inputValue.trim();
                const newValue = current ? `${current}, ${example}` : example;
                setInputValue(newValue);
              }}
            >
              {example}
            </Badge>
          ))}
        </Group>

        {/* Actions */}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            <Kbd size="xs">↵</Kbd> to apply
          </Text>
          <Group gap="xs">
            {hasDeadCards && (
              <Button variant="subtle" color="gray" size="sm" leftSection={<IconX size={16} />} onClick={handleClear}>
                Clear
              </Button>
            )}
            <Button size="sm" leftSection={<IconCheck size={16} />} onClick={handleApply}>
              Apply
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Display parsed cards with suit colors
 */
function ParsedCardsDisplay({ cards, inputValue }: { cards: string[]; inputValue: string }) {
  if (!inputValue.trim()) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xs">
        Enter cards to mark as dead (removed from deck)
      </Text>
    );
  }

  if (cards.length === 0) {
    return (
      <Text size="sm" c="red.5" ta="center" py="xs">
        No valid cards found
      </Text>
    );
  }

  return (
    <Group gap="xs" justify="center" py="xs" wrap="wrap">
      {cards.map((card) => (
        <CardBadge key={card} card={card} />
      ))}
      <Badge size="lg" variant="light" color="orange" ml="xs">
        {cards.length} card{cards.length !== 1 ? 's' : ''}
      </Badge>
    </Group>
  );
}

/**
 * Individual card badge with suit coloring
 */
function CardBadge({ card }: { card: string }) {
  const suit = card[1];
  const isRed = suit === 'h' || suit === 'd';

  return (
    <Badge
      size="lg"
      variant="outline"
      color={isRed ? 'red' : 'gray'}
      style={{ fontFamily: 'monospace', textTransform: 'none' }}
    >
      {formatCardDisplay(card)}
    </Badge>
  );
}

/**
 * Format card for display with suit symbol
 */
function formatCardDisplay(card: string): string {
  const rank = card[0];
  const suit = card[1];
  const suitSymbol: Record<string, string> = {
    c: '♣',
    d: '♦',
    h: '♥',
    s: '♠',
  };
  return `${rank}${suitSymbol[suit] || suit}`;
}
